import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

/**
 * Check if a time slot is occupied
 * @param {Object} slotStart - Start time of the slot (dayjs object)
 * @param {Object} slotEnd - End time of the slot (dayjs object)
 * @param {Array} occupiedSlots - Array of occupied time slots
 * @return {boolean} True if slot is occupied, false otherwise
 */
const isSlotOccupied = (slotStart, slotEnd, occupiedSlots) => {
  return occupiedSlots.some((occupied) => {
    const occupiedStart = dayjs(occupied.start_time);
    const occupiedEnd = dayjs(occupied.end_time);

    // Check for any overlap
    return (
      slotStart.isBetween(occupiedStart, occupiedEnd, null, '[)') ||
      slotEnd.isBetween(occupiedStart, occupiedEnd, null, '(]') ||
      (slotStart.isBefore(occupiedStart) && slotEnd.isAfter(occupiedEnd))
    );
  });
};

/**
 * Check if a time slot falls within a reject window
 * @param {Object} slotStart - Start time of the slot (dayjs object)
 * @param {Object} slotEnd - End time of the slot (dayjs object)
 * @param {Array} rejectWindows - Array of reject windows
 * @return {boolean} True if slot falls within reject window, false otherwise
 */
const isSlotRejected = (slotStart, slotEnd, rejectWindows) => {
  return rejectWindows.some((reject) => {
    const rejectStart = dayjs(reject.start_time);
    const rejectEnd = dayjs(reject.end_time);

    // Check if slot is completely within reject window
    return (
      slotStart.isBetween(rejectStart, rejectEnd, null, '[)') ||
      slotEnd.isBetween(rejectStart, rejectEnd, null, '(]') ||
      (slotStart.isBefore(rejectStart) && slotEnd.isAfter(rejectEnd))
    );
  });
};

/**
 * Divide availability windows into appointment slots
 * @param {Array} openWindows - Array of open availability windows
 * @param {number} consultationInterval - Length of each slot in minutes
 * @param {Array} occupiedSlots - Array of occupied time slots
 * @param {Array} rejectWindows - Array of reject windows
 * @param {string} targetTimezone - Timezone for display (optional)
 * @return {Array} Array of available time slots
 */
export const divideIntoTimeSlots = (
  openWindows,
  consultationInterval,
  occupiedSlots = [],
  rejectWindows = [],
  targetTimezone = null,
) => {
  const slots = [];

  openWindows.forEach((window) => {
    let current = dayjs(window.start_time);
    const end = dayjs(window.end_time);

    while (current.isBefore(end)) {
      const slotEnd = current.add(consultationInterval, 'minute');

      // Don't create slots that extend beyond the window
      if (slotEnd.isAfter(end)) {
        break;
      }

      // Check if slot is available
      if (
        !isSlotOccupied(current, slotEnd, occupiedSlots) &&
        !isSlotRejected(current, slotEnd, rejectWindows)
      ) {
        slots.push({
          start: targetTimezone ? current.tz(targetTimezone).format() : current.format(),
          end: targetTimezone ? slotEnd.tz(targetTimezone).format() : slotEnd.format(),
          startDisplay: targetTimezone
            ? current.tz(targetTimezone).format('YYYY-MM-DD HH:mm')
            : current.format('YYYY-MM-DD HH:mm'),
          endDisplay: targetTimezone ? slotEnd.tz(targetTimezone).format('HH:mm') : slotEnd.format('HH:mm'),
        });
      }

      current = slotEnd;
    }
  });

  return slots;
};

/**
 * Process doctor availability data from API response
 * @param {Object} doctorData - Doctor data from API
 * @param {string} patientTimezone - Patient's timezone
 * @return {Object} Processed doctor data with available slots
 */
export const processDoctorAvailability = (doctorData, patientTimezone) => {
  const userTimezone = patientTimezone || new Intl.DateTimeFormat().resolvedOptions().timeZone;

  const availableSlots = divideIntoTimeSlots(
    doctorData.open_windows,
    doctorData.consultation_interval,
    doctorData.occupied_slots,
    doctorData.reject_windows,
    userTimezone,
  );

  return {
    doctorId: doctorData.doctor_id,
    doctorName: doctorData.doctor_name,
    doctorTimezone: doctorData.doctor_timezone,
    consultationInterval: doctorData.consultation_interval,
    availableSlots,
    totalSlots: availableSlots.length,
  };
};

/**
 * Group slots by date
 * @param {Array} slots - Array of time slots
 * @return {Object} Slots grouped by date
 */
export const groupSlotsByDate = (slots) => {
  const grouped = {};

  slots.forEach((slot) => {
    const date = dayjs(slot.start).format('YYYY-MM-DD');
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(slot);
  });

  return grouped;
};

/**
 * Get appointment status display info
 * @param {string} status - Appointment status
 * @return {Object} Display information for status
 */
export const getAppointmentStatusDisplay = (status) => {
  const statusMap = {
    confirmed: {
      label: 'Confirmed',
      color: 'success',
      icon: '✓',
    },
    pending: {
      label: 'Pending',
      color: 'warning',
      icon: '⏳',
    },
    rejected: {
      label: 'Rejected',
      color: 'danger',
      icon: '✗',
    },
    transferred: {
      label: 'Transfer Pending',
      color: 'info',
      icon: '↔',
    },
  };

  return (
    statusMap[status] || {
      label: status,
      color: 'secondary',
      icon: '?',
    }
  );
};
